import type { Drop, GameState } from '@gra/engine';
import {
  AdditiveBlending,
  Color,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshStandardMaterial,
  type Object3D,
  type Scene,
  ShaderMaterial,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  TorusGeometry,
  Vector3,
} from 'three';
import type { Assets } from './assets';
import { type FxLayer, glow } from './fx';
import type { Particles } from './particles';
import { WORLD_SCALE as S } from './visuals';

const CHEST_MODEL = 'kaykit/dungeon/chest_gold.glb';
const CHEST_SCALE = 1;

// Moneta: niski walec z rantem — z boku widać grubość, w obrocie błyska złotem.
const coinGeo = new CylinderGeometry(0.3, 0.3, 0.08, 32).rotateX(Math.PI / 2);
const coinRimGeo = new TorusGeometry(0.3, 0.03, 8, 32);
const coinMat = new MeshStandardMaterial({ color: '#ffcf4a', metalness: 0.85, roughness: 0.28, emissive: '#7a4a00', emissiveIntensity: 0.6 });
const coinGlowMat = new SpriteMaterial({ map: glow(), color: '#ffc040', blending: AdditiveBlending, depthWrite: false, opacity: 0.45, toneMapped: false });

// Mikstura: kulista butelka z szyjką i korkiem.
const flaskGeo = new SphereGeometry(0.3, 24, 16);
const neckGeo = new CylinderGeometry(0.09, 0.1, 0.24, 14).translate(0, 0.36, 0);
const corkGeo = new CylinderGeometry(0.1, 0.09, 0.1, 12).translate(0, 0.52, 0);
const potionMat = new MeshStandardMaterial({ color: '#e0283c', emissive: '#a0101e', emissiveIntensity: 1.1, roughness: 0.12, transparent: true, opacity: 0.9 });
const glassMat = new MeshStandardMaterial({ color: '#d9e8f0', roughness: 0.1, transparent: true, opacity: 0.55 });
const corkMat = new MeshStandardMaterial({ color: '#8a5a30', roughness: 0.9 });
const potionGlowMat = new SpriteMaterial({ map: glow(), color: '#ff4058', blending: AdditiveBlending, depthWrite: false, opacity: 0.5, toneMapped: false });

// Przedmiot: słup światła w kolorze rzadkości i wirujący klejnot w środku — widać go z daleka.
const beamGeo = new CylinderGeometry(0.28, 0.4, 5, 24, 1, true).translate(0, 2.5, 0);
const itemGeo = new SphereGeometry(0.3, 6, 2);

function beamMaterial(color: Color): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: { color: { value: color }, time: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform vec3 color; uniform float time;
      varying vec2 vUv;
      void main() {
        float a = pow(1.0 - vUv.y, 1.6) * (0.55 + 0.45 * sin(vUv.x * 30.0 + time * 3.0 - vUv.y * 6.0));
        gl_FragColor = vec4(color * 1.8, clamp(a * 0.75, 0.0, 1.0));
      }`,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide,
  });
}

interface DropView {
  root: Group;
  spin: Object3D;
  born: number;
  beam?: ShaderMaterial;
  color: string;
}

interface ChestView {
  root: Group;
  born: number;
}

/** Skrzynie i łupy na ziemi (monety, mikstury, przedmioty). Synchronizowane ze stanem silnika po id. */
export class LootView {
  private readonly drops = new Map<number, DropView>();
  private readonly chests = new Map<number, ChestView>();
  private readonly opening: { root: Group; t: number }[] = [];
  private time = 0;

  constructor(
    private readonly scene: Scene,
    private readonly assets: Assets,
    private readonly state: GameState,
    private readonly fx: FxLayer,
    private readonly sparks: Particles,
  ) {}

  /** Kolor rzadkości przedmiotu z danych gry. */
  rarityColor(rarity: string | undefined): string {
    return this.state.content.rarities[rarity ?? '']?.color ?? '#ffffff';
  }

  update(dt: number): void {
    this.time += dt;
    this.syncChests();
    this.syncDrops();
    for (let i = this.opening.length - 1; i >= 0; i--) {
      const o = this.opening[i]!;
      o.t += dt;
      // Otwarta skrzynia podskakuje i zapada się pod ziemię.
      const k = o.t / 0.9;
      o.root.position.y = k < 0.25 ? Math.sin((k / 0.25) * Math.PI) * 0.35 : -(k - 0.25) * 1.4;
      o.root.scale.setScalar(CHEST_SCALE * (1 - Math.max(0, k - 0.4)));
      if (k >= 1) {
        o.root.removeFromParent();
        this.opening.splice(i, 1);
      }
    }
  }

  /** Otwarcie skrzyni: wybuch złotych iskier i błysk. */
  chestOpened(x: number, y: number): void {
    const at = new Vector3(x * S, 0.6, y * S);
    this.fx.flash(at, '#ffd060', 3.5, 0.4);
    this.sparks.emit(at, { color: '#ffd860', count: 40, speed: 5, life: 0.9, up: 4, gravity: 8, size: 0.2, hot: true });
    this.fx.shake = Math.max(this.fx.shake, 0.25);
  }

  private syncChests(): void {
    const seen = new Set<number>();
    for (const c of this.state.chests) {
      seen.add(c.id);
      let view = this.chests.get(c.id);
      if (!view) {
        const root = new Group();
        const model = this.assets.template(CHEST_MODEL).scene.clone();
        model.traverse((o) => {
          o.castShadow = true;
        });
        root.add(model);
        // Złota poświata nad skrzynią, żeby było ją widać między grobami.
        const halo = new Sprite(coinGlowMat);
        halo.scale.setScalar(2.4);
        halo.position.y = 0.6;
        root.add(halo);
        root.scale.setScalar(CHEST_SCALE);
        root.rotation.y = (c.id % 8) * 0.7;
        this.scene.add(root);
        view = { root, born: this.time };
        this.chests.set(c.id, view);
        // Pojawienie się: chmurka iskier.
        this.sparks.emit(new Vector3(c.x * S, 0.4, c.y * S), { color: '#ffe08a', count: 16, speed: 2, life: 0.8, up: 2, size: 0.16, hot: true });
      }
      const age = this.time - view.born;
      view.root.position.set(c.x * S, 0, c.y * S);
      view.root.scale.setScalar(CHEST_SCALE * Math.min(1, 0.2 + age * 2.5));
      // Co jakiś czas skrzynia „mruga” iskrą — przyciąga wzrok.
      if (Math.random() < 0.04) this.sparks.emit(new Vector3(c.x * S, 0.9, c.y * S), { color: '#ffd860', count: 1, speed: 0.6, life: 0.8, up: 1.2, size: 0.14, hot: true });
    }
    for (const [id, view] of this.chests) {
      if (seen.has(id)) continue;
      this.chests.delete(id);
      this.opening.push({ root: view.root, t: 0 });
    }
  }

  private createDrop(d: Drop): DropView {
    const root = new Group();
    let spin: Object3D;
    let beam: ShaderMaterial | undefined;
    let color = '#ffc040';
    if (d.kind === 'gold') {
      const coin = new Group();
      coin.add(new Mesh(coinGeo, coinMat), new Mesh(coinRimGeo, coinMat));
      coin.children.forEach((m) => (m.castShadow = true));
      const halo = new Sprite(coinGlowMat);
      halo.scale.setScalar(1.4);
      root.add(coin, halo);
      spin = coin;
    } else if (d.kind === 'potion') {
      const flask = new Group();
      flask.add(new Mesh(flaskGeo, potionMat), new Mesh(neckGeo, glassMat), new Mesh(corkGeo, corkMat));
      const halo = new Sprite(potionGlowMat);
      halo.scale.setScalar(1.8);
      root.add(flask, halo);
      spin = flask;
      color = '#ff4058';
    } else {
      color = this.rarityColor(d.rarity);
      const c = new Color(color);
      beam = beamMaterial(c);
      const gem = new Mesh(itemGeo, new MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 1.4, roughness: 0.2, metalness: 0.4, flatShading: true }));
      const halo = new Sprite(new SpriteMaterial({ map: glow(), color: c, blending: AdditiveBlending, depthWrite: false, opacity: 0.7, toneMapped: false }));
      halo.scale.setScalar(1.6);
      root.add(new Mesh(beamGeo, beam), gem, halo);
      spin = gem;
    }
    this.scene.add(root);
    return { root, spin, born: this.time, beam, color };
  }

  private syncDrops(): void {
    const seen = new Set<number>();
    for (const d of this.state.drops) {
      seen.add(d.id);
      let view = this.drops.get(d.id);
      if (!view) {
        view = this.createDrop(d);
        this.drops.set(d.id, view);
      }
      // Łup wyskakuje łukiem, odbija się od ziemi, potem unosi i obraca.
      const age = this.time - view.born;
      const pop = lootHop(age, 1.3);
      const hover = d.kind === 'item' || d.kind === 'relic' ? 0.7 : 0.4;
      view.root.position.set(d.x * S, hover + pop + Math.sin(this.time * 3 + d.id) * 0.08, d.y * S);
      view.spin.rotation.y = this.time * (d.kind === 'gold' ? 4 : 1.6) + d.id;
      view.spin.scale.setScalar(Math.min(1, 0.3 + age * 3));
      if (view.beam) view.beam.uniforms.time!.value = this.time;
    }
    for (const [id, view] of this.drops) {
      if (seen.has(id)) continue;
      this.sparks.emit(view.root.position, { color: view.color, count: 8, speed: 2, life: 0.4, up: 1.5, size: 0.16, hot: true });
      view.root.removeFromParent();
      view.beam?.dispose();
      this.drops.delete(id);
    }
  }
}

/** Wysokość podskoku łupu: łuk wyrzutu i jedno mniejsze odbicie od ziemi (sekundy od wypadnięcia). */
export function lootHop(age: number, height: number): number {
  if (age < 0.45) return Math.sin((age / 0.45) * Math.PI) * height;
  if (age < 0.7) return Math.sin(((age - 0.45) / 0.25) * Math.PI) * height * 0.25;
  return 0;
}
